import { Add } from "@mui/icons-material";
import { Stack, Typography } from "@mui/material";
import moment from "moment";
import { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { CRM_PRO_CREATE_TASK, CRM_PRO_DELETE_TASK, CRM_PRO_EDIT_TASK, CRM_PRO_UPDATE_TASK_PROGRESS } from "../../constants";
import { useHasPermission } from "../../utils";
import { LeadService } from "../../services";
import { CardLayout, DeleteModal, EmptyState, IconButton } from "../v3";
import { AddEditTask } from "./AddEditTask.component";
import { TaskCard } from "./TaskCard.component";
import { TaskProgressHistoryModal } from "./TaskProgressHistoryModal.component";
import { UpdateTaskProgress } from "./UpdateTaskProgress.component";

export const LeadTasks = ({ tasks = [], onUpdate = () => {} }: any) => {
  const [openTaskModal, setOpenTaskModal] = useState(false);
  const [openProgressModal, setOpenProgressModal] = useState(false);
  const [openProgressListModal, setOpenProgressListModal] = useState(false);
  const [taskData, setTaskData] = useState({ description: "", dueDate: moment().format("YYYY-MM-DD") });
  const [selectedProgressTask, setSelectedProgressTask] = useState<any>(null);
  const [taskProgressList, setTaskProgressList] = useState<any[]>([]);
  const [selectedTaskStatus, setSelectedTaskStatus] = useState<any>(null);
  const [selectedAssignee, setSelectedAssignee] = useState<any>(null);
  const [editingTask, setEditingTask] = useState<any>(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    task: null as any,
  });

  const { user } = useSelector((state: any) => state.user);

  const hasCreateTask = useHasPermission(CRM_PRO_CREATE_TASK);
  const hasEditTask = useHasPermission(CRM_PRO_EDIT_TASK);
  const hasDeleteTask = useHasPermission(CRM_PRO_DELETE_TASK);
  const hasUpdateTaskProgress = useHasPermission(CRM_PRO_UPDATE_TASK_PROGRESS);

  /** Only the assignee or the creator may update progress / see the button. */
  const canUpdateProgress = (task: any) =>
    hasUpdateTaskProgress && !!user && (user.id === task?.assignee?.id || user.id === task?.createdById);

  /** Completed tasks are read-only — hide edit & delete. */
  const isCompleted = (task: any) => task?.status === "DONE" || task?.isCompleted;

  const handleOpenTaskModal = () => {
    setEditingTask(null);
    setTaskData({ description: "", dueDate: moment().format("YYYY-MM-DD") });
    setSelectedTaskStatus(null);
    setSelectedAssignee(null);
    setOpenTaskModal(true);
  };

  const handleCloseTaskModal = () => {
    setEditingTask(null);
    setTaskData({ description: "", dueDate: moment().format("YYYY-MM-DD") });
    setSelectedTaskStatus(null);
    setSelectedAssignee(null);
    setOpenTaskModal(false);
  };

  const handleSaveTask = async (taskPayload: any) => {
    try {
      if (editingTask) {
        await LeadService.updateTask(editingTask.id, taskPayload);
        toast.success("Task updated successfully");
      } else {
        await LeadService.createTask(taskPayload);
        toast.success("Task added successfully");
      }

      onUpdate();
      handleCloseTaskModal();
    } catch (error) {
      const msg = error?.response?.data?.message || `Failed to ${editingTask ? "update" : "create"} task`;
      toast.error(typeof msg === "string" ? msg : Array.isArray(msg) ? msg.join(", ") : "An error occurred");
      console.error("handleSaveTask error:", error);
    }
  };

  const handleEdit = (task: any) => {
    if (isCompleted(task)) return; // guard — completed tasks are not editable
    setEditingTask(task);
    // Ensure dueDate is in YYYY-MM-DD format for the date input
    const formattedDueDate = task.dueDate ? moment(task.dueDate).format("YYYY-MM-DD") : "";
    setTaskData({ description: task.description || "", dueDate: formattedDueDate });
    setSelectedTaskStatus(task.leadStatus || { id: task.leadStatusId, name: task.title });
    setSelectedAssignee(task.assignee || null);
    setOpenTaskModal(true);
  };

  const openDeleteModal = (task: any) => {
    if (isCompleted(task)) return; // guard
    setDeleteModal({ isOpen: true, task });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, task: null });
  };

  const handleDeleteConfirm = async () => {
    const task = deleteModal.task;
    if (!task) return;

    await LeadService.deleteTask(task?.id);
    toast.success("Task deleted successfully");
    onUpdate();
    closeDeleteModal();
  };

  const handleOpenProgressModal = (task: any) => {
    if (!canUpdateProgress(task)) return;

    setSelectedProgressTask(task);
    setOpenProgressModal(true);
  };

  const handleCloseProgressModal = () => {
    setOpenProgressModal(false);
    setSelectedProgressTask(null);
  };

  const handleOpenProgressListModal = async (task: any) => {
    try {
      const progressList = await LeadService.getTaskProgress(task.id);
      setTaskProgressList(progressList);
      setSelectedProgressTask(task);
      setOpenProgressListModal(true);
    } catch (error) {
      toast.error("Failed to load progress updates");
      console.error("Error loading progress:", error);
    }
  };

  const handleCloseProgressListModal = () => {
    setOpenProgressListModal(false);
    setSelectedProgressTask(null);
    setTaskProgressList([]);
  };

  return (
    <>
      <CardLayout
        sx={{
          padding: "14px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle2" fontWeight={600}>
            Tasks
          </Typography>

          {hasCreateTask && (
            <IconButton
              buttonText="Add Task"
              tooltip="Add New Task"
              icon={<Add />}
              onClick={handleOpenTaskModal}
              styles={{
                color: "#757575",
                backgroundColor: "#F4F4F4",
              }}
              hoverStyles={{
                backgroundColor: "#EBE9F9",
              }}
            />
          )}
        </Stack>

        {tasks?.length === 0 ? (
          <EmptyState title="No tasks yet" subtitle="Get started by adding your first task!" />
        ) : (
          <Stack
            spacing={1.5}
            sx={{
              overflowY: "auto",
              flex: 1,
              pr: 1,

              // optional scrollbar styling
              "&::-webkit-scrollbar": {
                width: "6px",
              },

              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#C1C1C1",
                borderRadius: "10px",
              },
            }}
          >
            {tasks.map((task: any) => (
              <TaskCard
                key={task.id}
                task={task}
                canUpdateProgress={canUpdateProgress(task)}
                canEdit={hasEditTask && !isCompleted(task)}
                canDelete={hasDeleteTask && !isCompleted(task)}
                onUpdateProgress={handleOpenProgressModal}
                onViewProgress={handleOpenProgressListModal}
                onEdit={handleEdit}
                onDelete={openDeleteModal}
              />
            ))}
          </Stack>
        )}
      </CardLayout>
      <AddEditTask
        open={openTaskModal}
        onClose={handleCloseTaskModal}
        taskData={taskData}
        setTaskData={setTaskData}
        selectedTaskStatus={selectedTaskStatus}
        setSelectedTaskStatus={setSelectedTaskStatus}
        selectedAssignee={selectedAssignee}
        setSelectedAssignee={setSelectedAssignee}
        onSuccess={handleSaveTask}
        isEdit={!!editingTask}
      />
      <UpdateTaskProgress
        open={openProgressModal}
        task={selectedProgressTask}
        handleCloseModal={handleCloseProgressModal}
        onSuccess={onUpdate}
      />
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        message="Are you sure you want to delete this task?"
      />

      <TaskProgressHistoryModal
        open={openProgressListModal}
        task={selectedProgressTask}
        progressList={taskProgressList}
        onClose={handleCloseProgressListModal}
      />
    </>
  );
};
